import { supabase } from '../lib/supabase';
import { toTurkishTitleCase } from '../utils/turkishValidation';

/**
 * Admin Order Service - Handles order management operations
 * Database schema: orders and order_items tables
 * Provides full order management for admin dashboard
 */

export const adminOrderService = {
  /**
   * Get all orders with order items and product details
   * @returns {Promise<Array>} Array of orders with items
   */
  async getAllOrders() {
    try {
      const { data, error } = await supabase
        ?.from('orders')
        ?.select(`
          *,
          order_items (
            *,
            products (*)
          ),
          user_profiles (
            id,
            full_name,
            email
          )
        `)
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(order => ({
        id: order?.id,
        userId: order?.user_id,
        guestEmail: order?.guest_email,
        userEmail: order?.user_profiles?.email || null,
        full_name: order?.full_name || null,
        user_profile: order?.user_profiles ? { full_name: order?.user_profiles?.full_name || null } : null,
        totalAmount: parseFloat(order?.total_amount),
        status: order?.status,
        paymentStatus: order?.payment_status,
        paymentMethod: order?.payment_method || 'credit_card',
        shippingAddress: order?.shipping_address,
        shipping_address: order?.shipping_address,
        tracking_company: order?.tracking_company || null,
        tracking_number: order?.tracking_number || null,
        cancel_reason: order?.cancel_reason || null,
        createdAt: order?.created_at,
        items: order?.order_items?.map(item => ({
          id: item?.id,
          productId: item?.product_id,
          quantity: item?.quantity,
          priceAtPurchase: parseFloat(item?.price_at_purchase),
          selectedColor: item?.selected_color || null,
          selectedSize: item?.selected_size || null,
          variantId: item?.variant_id || null,
          product: item?.products ? {
            id: item?.products?.id,
            title: item?.products?.title,
            images: item?.products?.images || [],
            image: item?.products?.images?.[0] || null
          } : null
        })) || []
      }));
    } catch (error) {
      console.error('Error fetching all orders:', error);
      throw error;
    }
  },

  /**
   * Get single order with full details
   * @param {string} orderId - Order UUID
   * @returns {Promise<Object>} Order details
   */
  async getOrderById(orderId) {
    try {
      const { data, error } = await supabase
        ?.from('orders')
        ?.select(`
          *,
          order_items (
            *,
            products (*)
          ),
          user_profiles (
            id,
            full_name,
            email
          )
        `)
        ?.eq('id', orderId)
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        userId: data?.user_id,
        guestEmail: data?.guest_email,
        userEmail: data?.user_profiles?.email || null,
        full_name: data?.full_name || null,
        fullName: data?.full_name || data?.user_profiles?.full_name || null,
        profiles: data?.user_profiles ? { full_name: data?.user_profiles?.full_name || null } : null,
        totalAmount: parseFloat(data?.total_amount),
        status: data?.status,
        paymentStatus: data?.payment_status,
        paymentMethod: data?.payment_method || 'credit_card',
        shippingAddress: data?.shipping_address,
        tracking_company: data?.tracking_company || null,
        tracking_number: data?.tracking_number || null,
        cancel_reason: data?.cancel_reason || null,
        createdAt: data?.created_at,
        items: data?.order_items?.map(item => ({
          id: item?.id,
          productId: item?.product_id,
          quantity: item?.quantity,
          priceAtPurchase: parseFloat(item?.price_at_purchase),
          selectedColor: item?.selected_color || null,
          selectedSize: item?.selected_size || null,
          variantId: item?.variant_id || null,
          product: item?.products ? {
            id: item?.products?.id,
            title: item?.products?.title,
            description: item?.products?.description,
            images: item?.products?.images || [],
            image: item?.products?.images?.[0] || null,
            category: item?.products?.category
          } : null
        })) || []
      };
    } catch (error) {
      console.error('Error fetching order by ID:', error);
      throw error;
    }
  },

  /**
   * Update order status
   * @param {string} orderId - Order ID
   * @param {string} status - New status
   * @returns {Promise<Object>} Updated order
   */
  async updateOrderStatus(orderId, status) {
    try {
      const { data, error } = await supabase
        ?.from('orders')
        ?.update({ status })
        ?.eq('id', orderId)
        ?.select()
        ?.single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Error updating order status:', error);
      throw error;
    }
  },

  /**
   * Approve bank transfer payment: set payment_status to 'completed' and trigger Mysoft invoice
   * @param {string} orderId - Order ID
   * @returns {Promise<{success: boolean, invoiceTriggered: boolean}>}
   */
  async approveBankTransferPayment(orderId) {
    try {
      // Step 1: Update payment_status to 'completed'
      const { error: updateError } = await supabase
        ?.from('orders')
        ?.update({ payment_status: 'completed' })
        ?.eq('id', orderId);

      if (updateError) throw updateError;

      // Step 2: Trigger Mysoft invoice (same as credit card flow)
      let invoiceTriggered = false;
      try {
        const { data: { session } } = await supabase?.auth?.getSession();
        const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
        const anonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;

        const invoiceRes = await window.fetch(
          `${supabaseUrl}/functions/v1/send-mysoft-invoice`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'apikey': anonKey,
              'Authorization': `Bearer ${session?.access_token || anonKey}`,
            },
            body: JSON.stringify({ orderId }),
          }
        );

        const invoiceData = await invoiceRes?.json();
        console.log('[approveBankTransferPayment] Mysoft invoice response:', invoiceData);
        invoiceTriggered = true;
      } catch (invoiceErr) {
        // Log but don't fail — payment approval is the critical step
        console.error('[approveBankTransferPayment] Mysoft invoice trigger failed:', invoiceErr);
      }

      return { success: true, invoiceTriggered };
    } catch (error) {
      console.error('Error approving bank transfer payment:', error);
      throw error;
    }
  },

  /**
   * Manually send an order to Mysoft (trigger invoice generation)
   * @param {string} orderId - Order ID
   * @returns {Promise<{success: boolean, data: any}>}
   */
  async sendToMysoft(orderId) {
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
      const anonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;

      // Fetch the full order row so we can deep-search it
      const { data: order, error: orderError } = await supabase
        ?.from('orders')
        ?.select('*')
        ?.eq('id', orderId)
        ?.single();

      if (orderError || !order) {
        throw new Error(`Order not found: ${orderError?.message}`);
      }

      // Convert the entire order object (including all nested JSONs) into a single string
      const orderString = JSON.stringify(order);

      // 1. Aggressive Email Extraction (Finds any valid email format in the string)
      const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/;
      const emailMatch = orderString?.match(emailRegex);
      let customerEmail = emailMatch ? emailMatch?.[0] : 'info@paddleturkiye.com';

      // 2. Aggressive TC/VKN Extraction
      // Matches exactly 11 digits that do NOT start with '0' (prevents grabbing phone numbers)
      const tcRegex = /(?:^|[^\d])([1-9]\d{10})(?:[^\d]|$)/;
      const tcMatch = orderString?.match(tcRegex);
      let customerTc = tcMatch ? tcMatch?.[1] : '11111111111';

      // Determine invoice scenario based on TC/VKN digit length
      // VKN = 10 digits (corporate) → TİCARİ FATURA
      // TCKN = 11 digits (individual) → EARSIVFATURA
      const cleanTc = String(customerTc || '11111111111')?.replace(/\D/g, '');
      const isCorporate = cleanTc?.length === 10;
      const invoiceScenario = isCorporate ? "TİCARİ FATURA" : "EARSIVFATURA";

      console.log('[sendToMysoft] Extracted email:', customerEmail, '| TC:', customerTc, '| invoiceScenario:', invoiceScenario);

      // 3. Extract and sanitize buyer name/address with Turkish Title Case
      // This retroactively fixes historical orders regardless of original casing
      let shippingDetails = null;
      try {
        const rawShipping = order?.shipping_address;
        shippingDetails = typeof rawShipping === 'string' ? JSON.parse(rawShipping) : rawShipping;
      } catch (_) {}

      const rawBuyerName = order?.full_name
        || shippingDetails?.fullName
        || shippingDetails?.ContactName
        || shippingDetails?.AdSoyad
        || null;

      const rawBuyerAddress = order?.address
        || shippingDetails?.address
        || shippingDetails?.Address
        || shippingDetails?.streetName
        || null;

      const sanitizedBuyerName = rawBuyerName ? toTurkishTitleCase(rawBuyerName) : null;
      const sanitizedBuyerAddress = rawBuyerAddress ? toTurkishTitleCase(rawBuyerAddress) : null;

      // Fetch order_items to pass as cart data — enables explicit store/event fork in edge function
      let cartItems = [];
      try {
        const { data: items } = await supabase
          ?.from('order_items')
          ?.select('*, products(title, vat_rate, sku)')
          ?.eq('order_id', orderId);
        if (items && items?.length > 0) {
          cartItems = items?.map(item => ({
            title: item?.products?.title || item?.title || 'Ürün',
            quantity: item?.quantity || 1,
            price: parseFloat(item?.price_at_purchase || item?.price || 0),
            selectedColor: item?.selected_color || null,
            selectedSize: item?.selected_size || null,
            productId: item?.product_id || null,
          }));
        } else {
          // FALLBACK FOR HISTORICAL ORDERS: check JSON fields stored directly on the order row
          // Old DB schema may have stored cart items under cart, order_items, or items fields
          const rawCartData = order?.cart || order?.order_items || order?.items || null;
          const parsedCart = (() => {
            if (!rawCartData) return null;
            if (Array.isArray(rawCartData)) return rawCartData;
            if (typeof rawCartData === 'string') {
              try { return JSON.parse(rawCartData); } catch { return null; }
            }
            return null;
          })();
          if (Array.isArray(parsedCart) && parsedCart?.length > 0) {
            cartItems = parsedCart?.map(item => ({
              title: item?.title || item?.name || item?.product_name || 'Ürün',
              quantity: Number(item?.quantity || item?.qty || 1),
              price: parseFloat(item?.price || item?.price_at_purchase || 0),
              selectedColor: item?.selectedColor || item?.selected_color || null,
              selectedSize: item?.selectedSize || item?.selected_size || null,
              productId: item?.productId || item?.product_id || null,
            }));
            console.log('[sendToMysoft] Fallback: loaded', cartItems?.length, 'items from order JSON fields (cart/order_items/items)');
          }
        }
      } catch (cartErr) {
        console.warn('[sendToMysoft] Could not fetch order_items for cart payload:', cartErr);
      }

      const res = await window.fetch(
        `${supabaseUrl}/functions/v1/send-mysoft-invoice`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': anonKey,
            'Authorization': `Bearer ${session?.access_token || anonKey}`,
          },
          body: JSON.stringify({
            orderId,
            customerEmail,
            customerTc,
            invoiceScenario,
            cart: cartItems,
            // Pre-sanitized buyer fields (Turkish Title Case) for retroactive invoice formatting
            ...(sanitizedBuyerName && { buyerName: sanitizedBuyerName }),
            ...(sanitizedBuyerAddress && { buyerAddress: sanitizedBuyerAddress }),
          }),
        }
      );

      const data = await res?.json();
      console.log('[sendToMysoft] Response:', data);

      if (!res?.ok) {
        throw new Error(data?.error || data?.message || 'Mysoft isteği başarısız oldu');
      }

      return { success: true, data };
    } catch (error) {
      console.error('[sendToMysoft] Error:', error);
      throw error;
    }
  },

  /**
   * Get order statistics
   * @returns {Promise<Object>} Order stats
   */
  async getOrderStats() {
    try {
      const { data, error } = await supabase
        ?.from('orders')
        ?.select('status, total_amount');

      if (error) throw error;

      const stats = {
        total: data?.length || 0,
        pending: data?.filter(o => o?.status === 'pending')?.length || 0,
        processing: data?.filter(o => o?.status === 'processing')?.length || 0,
        shipped: data?.filter(o => o?.status === 'shipped')?.length || 0,
        delivered: data?.filter(o => o?.status === 'delivered')?.length || 0,
        cancelled: data?.filter(o => o?.status === 'cancelled')?.length || 0,
        totalRevenue: data?.reduce((sum, order) => sum + parseFloat(order?.total_amount || 0), 0)
      };

      return stats;
    } catch (error) {
      console.error('Error fetching order stats:', error);
      throw error;
    }
  }
};